/**
 * IncidentsSummaryCard — Dashboard tile showing open incident counts
 * by severity + days-since-last-injury. Silent-when-clean: renders
 * nothing when there are zero open incidents and no logged injuries,
 * so a spotless team gets a spotless dashboard.
 *
 * Milestone celebration: crossing round-number safety streaks (365,
 * 500, 1000, 2000, 5000, 10000 days) fires a one-shot confetti burst
 * + shows a permanent inline badge on the tile. The "last seen"
 * milestone is stored in localStorage so a page refresh doesn't
 * re-trigger the confetti — but a team that hits 500 days and comes
 * back next month at 1000 sees a fresh celebration.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldAlert, HeartPulse, ChevronRight, Trophy } from "lucide-react";

// Ordered ascending — a team hitting 1200 days celebrates the 1000
// milestone (not 500), and 500 remains "last seen" until they roll
// past 1000. Two-year (~730) is deliberately absent because it lands
// too close to 1000; add if user requests.
const MILESTONES = [365, 500, 1000, 2000, 5000, 10000];
const MILESTONE_LABELS = {
  365: "1 Year Recordable-Free",
  500: "500 Days Recordable-Free",
  1000: "1,000 Days Recordable-Free",
  2000: "2,000 Days Recordable-Free",
  5000: "5,000 Days Recordable-Free",
  10000: "10,000 Days Recordable-Free",
};

function highestMilestoneAtOrBelow(days) {
  if (days == null) return null;
  let hit = null;
  for (const m of MILESTONES) {
    if (days >= m) hit = m;
    else break;
  }
  return hit;
}

function fireConfetti() {
  // Two staggered bursts from bottom corners — feels like a stadium
  // reveal, not a birthday card. Emerald + gold to match the KPI tile
  // and evoke a trophy vibe.
  const colors = ["#10b981", "#34d399", "#facc15", "#fbbf24", "#f8fafc"];
  const opts = { spread: 55, startVelocity: 45, ticks: 220, gravity: 0.9, scalar: 1.1, colors };
  confetti({ ...opts, particleCount: 90, angle: 60, origin: { x: 0, y: 1 } });
  confetti({ ...opts, particleCount: 90, angle: 120, origin: { x: 1, y: 1 } });
  setTimeout(() => {
    confetti({ ...opts, particleCount: 40, angle: 90, origin: { x: 0.5, y: 1 }, spread: 90 });
  }, 250);
}

export default function IncidentsSummaryCard() {
  const [data, setData] = useState(null);
  const [celebratingMilestone, setCelebratingMilestone] = useState(null);
  const firedRef = useRef(false);

  useEffect(() => {
    api.get("/incidents/dashboard")
      .then((r) => setData(r.data))
      .catch(() => setData({ total_open: 0, days_since_last_injury: null }));
  }, []);

  const currentMilestone = useMemo(
    () => highestMilestoneAtOrBelow(data?.days_since_last_injury),
    [data?.days_since_last_injury],
  );

  useEffect(() => {
    // Fire confetti at most once per session per new milestone. Uses
    // localStorage so a team that just crossed 1000 doesn't re-fire on
    // every navigation; a team crossing 1000 → 2000 does re-fire.
    if (firedRef.current || currentMilestone == null) return;
    let lastSeen = null;
    try { lastSeen = Number(localStorage.getItem("safety_milestone_last_seen")) || null; } catch { /* ignore */ }
    if (lastSeen && lastSeen >= currentMilestone) {
      setCelebratingMilestone(currentMilestone); // still show the badge, just don't confetti again
      firedRef.current = true;
      return;
    }
    firedRef.current = true;
    setCelebratingMilestone(currentMilestone);
    // Delay a beat so it happens after the card animates in — feels
    // reactive to the number rather than the page load.
    const t = setTimeout(() => {
      fireConfetti();
      try { localStorage.setItem("safety_milestone_last_seen", String(currentMilestone)); } catch { /* ignore */ }
    }, 450);
    return () => clearTimeout(t);
  }, [currentMilestone]);

  if (!data) return null;
  const hasOpen = data.total_open > 0;
  const hasInjuryHistory = data.days_since_last_injury !== null && data.days_since_last_injury !== undefined;
  if (!hasOpen && !hasInjuryHistory) return null;

  const sev = data.open_by_severity || {};
  const days = data.days_since_last_injury;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6" data-testid="incidents-summary-cards">
      <Link to="/incidents" className="block">
        <Card className={`transition-colors ${hasOpen ? "bg-red-950/30 border-red-900 hover:bg-red-950/40" : "bg-slate-900 border-slate-800 hover:bg-slate-800/60"}`} data-testid="dashboard-incidents-open">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">Open Incidents</div>
                <div className="font-display font-black text-4xl tracking-tight mt-2 leading-none">{data.total_open}</div>
                {hasOpen && (
                  <div className="text-xs text-slate-400 mt-2 flex gap-3 flex-wrap">
                    {sev.critical > 0 && <span className="text-red-300 font-bold">{sev.critical} critical</span>}
                    {sev.high > 0 && <span className="text-red-300">{sev.high} high</span>}
                    {sev.medium > 0 && <span className="text-amber-300">{sev.medium} med</span>}
                    {sev.low > 0 && <span className="text-slate-400">{sev.low} low</span>}
                  </div>
                )}
                {!hasOpen && <div className="text-xs text-emerald-400 mt-2">All clear.</div>}
              </div>
              <div className={`p-2.5 rounded-md border ${hasOpen ? "border-red-900 bg-red-950/60 text-red-300" : "border-slate-800 bg-slate-950 text-slate-400"}`}>
                <ShieldAlert className="w-4 h-4" />
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-400 mt-3">
              View all incidents <ChevronRight className="w-3 h-3" />
            </div>
          </CardContent>
        </Card>
      </Link>

      {hasInjuryHistory && (
        <Card
          className={`transition-colors ${celebratingMilestone ? "bg-gradient-to-br from-emerald-950/60 via-slate-900 to-slate-900 border-emerald-800" : "bg-slate-900 border-slate-800"}`}
          data-testid="dashboard-days-since-injury"
        >
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">Days Since Last Recordable</div>
                <div className={`font-display font-black text-4xl tracking-tight mt-2 leading-none ${days >= 30 ? "text-emerald-400" : "text-amber-400"}`}>
                  {days.toLocaleString()}
                </div>
                <div className="text-xs text-slate-400 mt-2">
                  {days >= 30 ? "Keep it up." : "Recent event — stay sharp."}
                </div>
                {celebratingMilestone && (
                  <div
                    className="mt-3 inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-emerald-500/15 border border-emerald-600/50 text-emerald-300 text-[10px] uppercase tracking-wider font-display font-bold"
                    data-testid="safety-milestone-badge"
                  >
                    <Trophy className="w-3 h-3" />
                    {MILESTONE_LABELS[celebratingMilestone]}
                  </div>
                )}
              </div>
              <div className={`p-2.5 rounded-md border shrink-0 ${celebratingMilestone ? "border-emerald-700 bg-emerald-950/50 text-emerald-300" : "border-slate-800 bg-slate-950 text-slate-400"}`}>
                <HeartPulse className="w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
